import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, Building2, Palette, ShieldCheck, Wrench, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";

function Field({ label, value, onChange, type = "text", placeholder = "", testId }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-wide text-zinc-400">{label}</Label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || label}
        data-testid={testId}
        className="bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500 focus-visible:ring-amber-500/40 focus-visible:border-amber-500/60"
      />
    </div>
  );
}

function Flag({ label, value, onChange, locked, testId, description }) {
  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-zinc-800 last:border-b-0">
      <div>
        <div className="text-sm text-white">{label} {locked && <span className="ml-1 text-[10px] uppercase tracking-wide text-zinc-500">(Phase B+)</span>}</div>
        {description && <div className="text-[11px] text-zinc-500 mt-0.5">{description}</div>}
      </div>
      <Switch
        checked={!!value}
        onCheckedChange={onChange}
        disabled={locked}
        data-testid={testId}
        className="data-[state=checked]:bg-amber-500 data-[state=unchecked]:bg-zinc-700"
      />
    </div>
  );
}

export default function SettingsView() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const c = await crmApi.getTenantConfig();
        if (active) setCfg(c);
      } catch (exc) {
        if (active) toast.error(exc.message || "Failed to load tenant config");
      } finally { if (active) setLoading(false); }
    })();
    return () => { active = false; };
  }, []);

  if (loading || !cfg) {
    return <div className="text-sm text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading settings…</div>;
  }

  const setBranding = (patch) => setCfg({ ...cfg, branding: { ...cfg.branding, ...patch } });
  const setFlag = (key, value) => setCfg({ ...cfg, feature_flags: { ...cfg.feature_flags, [key]: value } });
  const setCompliance = (patch) => setCfg({ ...cfg, compliance: { ...cfg.compliance, ...patch } });
  const setSuppliers = (patch) => setCfg({ ...cfg, suppliers: { ...cfg.suppliers, ...patch } });

  const save = async () => {
    setSaving(true);
    try {
      const updated = await crmApi.updateTenantConfig({
        branding: cfg.branding,
        feature_flags: cfg.feature_flags,
        compliance: cfg.compliance,
        suppliers: cfg.suppliers,
      });
      setCfg(updated);
      toast.success("Tenant config saved");
    } catch (exc) {
      toast.error(exc.message || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="owner-settings">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tenant Settings</h1>
          <p className="text-sm text-zinc-400 mt-1">White-label config for <span className="text-white">{cfg.tenant_id}</span>. Branding, feature flags, compliance defaults, and supplier preferences.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="save-settings-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save changes</>}
        </Button>
      </div>

      <Card className="bg-zinc-900/40 border-zinc-800" data-testid="branding-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><Palette className="h-4 w-4 text-amber-300" /> Branding</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <Field label="Public name" value={cfg.branding.public_name} onChange={(v) => setBranding({ public_name: v })} testId="branding-public-name" />
          <Field label="Support email" value={cfg.branding.support_email} onChange={(v) => setBranding({ support_email: v })} testId="branding-support-email" />
          <Field label="Support phone" value={cfg.branding.support_phone} onChange={(v) => setBranding({ support_phone: v })} testId="branding-support-phone" />
          <Field label="Logo URL" value={cfg.branding.logo_url} onChange={(v) => setBranding({ logo_url: v })} />
          <Field label="Primary color (hex)" value={cfg.branding.primary_color_hex} onChange={(v) => setBranding({ primary_color_hex: v })} testId="branding-primary-color" />
          <Field label="Accent color (hex)" value={cfg.branding.accent_color_hex} onChange={(v) => setBranding({ accent_color_hex: v })} testId="branding-accent-color" />
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/40 border-zinc-800" data-testid="flags-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><Sparkles className="h-4 w-4 text-amber-300" /> Feature flags</CardTitle></CardHeader>
        <CardContent>
          <Flag label="Maintenance plans" value={cfg.feature_flags.enable_maintenance_plans} onChange={(v) => setFlag("enable_maintenance_plans", v)} description="Subscription tiers, member portal, post-job attach." testId="flag-plans" />
          <Flag label="Pest partner benefit (Plus / Premier)" value={cfg.feature_flags.enable_pest_partner} onChange={(v) => setFlag("enable_pest_partner", v)} description="Bi-monthly Duo Pest Defense dispatch on the upper tiers." testId="flag-pest" />
          <Flag label="Home Depot deep-link workflow" value={cfg.feature_flags.enable_home_depot_workflow} onChange={(v) => setFlag("enable_home_depot_workflow", v)} description="Casey's takeoff → HD Material List Builder brief." testId="flag-hd" />
          <Flag label="White-label resale" value={cfg.feature_flags.enable_white_label_resale} onChange={(v) => setFlag("enable_white_label_resale", v)} description="Allow this tenant to create sub-tenants." testId="flag-resale" />
          <Flag label="Outbound SMS (Twilio + A2P 10DLC)" value={cfg.feature_flags.enable_outbound_sms} onChange={(v) => setFlag("enable_outbound_sms", v)} locked description="Requires Twilio + approved 10DLC campaign — lights up in Phase B." testId="flag-sms" />
          <Flag label="Outbound email (SendGrid + DKIM)" value={cfg.feature_flags.enable_outbound_email} onChange={(v) => setFlag("enable_outbound_email", v)} locked description="Requires SendGrid + verified domain — lights up in Phase B." testId="flag-email" />
          <Flag label="Calendar booking (Google OAuth)" value={cfg.feature_flags.enable_calendar_booking} onChange={(v) => setFlag("enable_calendar_booking", v)} locked description="Requires Google Cloud OAuth — lights up in Phase B." testId="flag-cal" />
          <Flag label="Property enrichment (Regrid/Estated/ATTOM)" value={cfg.feature_flags.enable_property_enrichment} onChange={(v) => setFlag("enable_property_enrichment", v)} locked description="Requires property-data provider — Phase B." testId="flag-property" />
          <Flag label="Storm watcher" value={cfg.feature_flags.enable_storm_watcher} onChange={(v) => setFlag("enable_storm_watcher", v)} locked description="NOAA + hail/wind event auto-prospecting — Phase B." testId="flag-storm" />
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/40 border-zinc-800" data-testid="compliance-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-amber-300" /> Compliance defaults</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <Field label="State code" value={cfg.compliance.state_code} onChange={(v) => setCompliance({ state_code: v.toUpperCase().slice(0, 2) })} />
          <Field label="Contractor reg number" value={cfg.compliance.contractor_registration_number} onChange={(v) => setCompliance({ contractor_registration_number: v })} testId="compliance-reg-number" />
          <Field label="Quiet hours start" type="number" value={cfg.compliance.quiet_hours_start} onChange={(v) => setCompliance({ quiet_hours_start: Number(v) })} />
          <Field label="Quiet hours end" type="number" value={cfg.compliance.quiet_hours_end} onChange={(v) => setCompliance({ quiet_hours_end: Number(v) })} />
          <div className="sm:col-span-2 space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-zinc-400">Plan marketing disclosure</Label>
            <Textarea
              rows={3}
              value={cfg.compliance.plan_marketing_disclosure || ""}
              onChange={(e) => setCompliance({ plan_marketing_disclosure: e.target.value })}
              data-testid="plan-disclosure"
              placeholder="Plan marketing disclosure"
              className="bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500 focus-visible:ring-amber-500/40 focus-visible:border-amber-500/60"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-zinc-900/40 border-zinc-800" data-testid="suppliers-card">
        <CardHeader className="pb-2"><CardTitle className="text-sm text-white flex items-center gap-2"><Wrench className="h-4 w-4 text-amber-300" /> Supplier preferences</CardTitle></CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-3">
          <Field label="Primary lumber" value={cfg.suppliers.primary_lumber} onChange={(v) => setSuppliers({ primary_lumber: v })} />
          <Field label="Primary roofing" value={cfg.suppliers.primary_roofing} onChange={(v) => setSuppliers({ primary_roofing: v })} />
          <Field label="Primary drywall" value={cfg.suppliers.primary_drywall} onChange={(v) => setSuppliers({ primary_drywall: v })} />
          <Field label="Primary electrical" value={cfg.suppliers.primary_electrical} onChange={(v) => setSuppliers({ primary_electrical: v })} />
          <Field label="Primary plumbing" value={cfg.suppliers.primary_plumbing} onChange={(v) => setSuppliers({ primary_plumbing: v })} />
        </CardContent>
      </Card>
    </div>
  );
}
